import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, X, Beaker, AlertTriangle, CheckCircle2, XCircle, MinusCircle, History, ChevronDown, ChevronUp, PenLine, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";

function ScoreGauge({ score }) {
  const pct = ((score + 100) / 200) * 100;
  const getColor = (s) => {
    if (s <= -50) return "#DC2626";
    if (s <= -20) return "#EA580C";
    if (s <= 20) return "#EAB308";
    if (s <= 50) return "#65A30D";
    return "#16A34A";
  };
  const color = getColor(score);
  const label = score <= -50 ? "Dangereux" : score <= -20 ? "Incompatible" : score <= 20 ? "Neutre" : score <= 50 ? "Compatible" : "Synergie";

  return (
    <div className="space-y-2" data-testid="compatibility-score-gauge">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-3xl font-black" style={{ color, fontFamily: "Manrope, sans-serif" }}>{score > 0 ? "+" : ""}{score}</p>
          <p className="text-xs font-semibold mt-0.5" style={{ color }}>{label}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px]" style={{ color: "#7A7A7A" }}>-100</p>
          <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Nefaste</p>
        </div>
      </div>
      <div className="relative h-3 rounded-full overflow-hidden" style={{ background: "#F3F4F6" }}>
        <div className="absolute inset-0 rounded-full" style={{
          background: "linear-gradient(90deg, #DC2626 0%, #EA580C 25%, #EAB308 50%, #65A30D 75%, #16A34A 100%)",
          opacity: 0.2,
        }} />
        <div className="absolute top-0 bottom-0 w-1 rounded-full transition-all duration-700"
          style={{ left: `calc(${pct}% - 2px)`, background: color, boxShadow: `0 0 6px ${color}60` }} />
      </div>
      <div className="flex justify-between">
        <span className="text-[9px]" style={{ color: "#AAAAAA" }}>-100</span>
        <span className="text-[9px]" style={{ color: "#AAAAAA" }}>0</span>
        <span className="text-[9px]" style={{ color: "#AAAAAA" }}>+100</span>
      </div>
    </div>
  );
}

function InteractionCard({ interaction }) {
  const scoreColor = interaction.score_paire <= -20 ? "#DC2626" : interaction.score_paire <= 20 ? "#EAB308" : "#16A34A";
  const typeLabels = {
    synergie: { label: "Synergie", bg: "#16A34A15", color: "#16A34A" },
    neutre: { label: "Neutre", bg: "#EAB30815", color: "#92700E" },
    antagonisme: { label: "Antagonisme", bg: "#EA580C15", color: "#EA580C" },
    incompatibilite_physique: { label: "Incomp. physique", bg: "#DC262615", color: "#DC2626" },
    risque_phytotoxicite: { label: "Phytotoxicite", bg: "#DC262615", color: "#DC2626" },
  };
  const typeInfo = typeLabels[interaction.type] || typeLabels.neutre;

  return (
    <div className="p-3 rounded-lg border border-black/5" data-testid="interaction-card">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold" style={{ color: "#1A1A1A" }}>{interaction.produit_a}</span>
          <span className="text-[10px]" style={{ color: "#AAAAAA" }}>x</span>
          <span className="text-[11px] font-semibold" style={{ color: "#1A1A1A" }}>{interaction.produit_b}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: typeInfo.bg, color: typeInfo.color }}>{typeInfo.label}</Badge>
          <span className="text-xs font-bold" style={{ color: scoreColor }}>{interaction.score_paire > 0 ? "+" : ""}{interaction.score_paire}</span>
        </div>
      </div>
      <p className="text-[11px] leading-relaxed" style={{ color: "#4A4A4A" }}>{interaction.detail}</p>
    </div>
  );
}

export default function CompatibilityTab({ parcelId, ilotId, refData, cultureType }) {
  const [tab, setTab] = useState("analyze");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [culture, setCulture] = useState(cultureType || "");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [customProductName, setCustomProductName] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [fullCatalog, setFullCatalog] = useState(null); // tous produits France (region-agnostic)

  // Charge la base de produits complète (France entière + amendements/engrais)
  // pour la compatibilité, indépendamment de la région courante.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/carnet/compatibility-products`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setFullCatalog(data.products || []);
      } catch (err) {
        console.warn('[CompatibilityTab] full catalog fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build unified product list — préfère le catalogue France complet, fallback refData
  const allProducts = [];
  if (fullCatalog && fullCatalog.length > 0) {
    for (const p of fullCatalog) {
      allProducts.push(p);
    }
  } else {
    // Fallback initial (avant chargement) sur refData
    if (refData?.produits_phyto) {
      for (const [cat, items] of Object.entries(refData.produits_phyto)) {
        for (const p of items) {
          allProducts.push({ ...p, source: "phyto", sourceLabel: cat });
        }
      }
    }
    if (refData?.biostimulants) {
      for (const p of refData.biostimulants) {
        allProducts.push({ ...p, source: "biostimulant", sourceLabel: "biostimulant", category: "biostimulant" });
      }
    }
  }

  const searchItems = allProducts.map(p => ({
    value: p.name,
    label: p.name,
    sublabel: `${p.amm || ''} ${(p.matiere_active || '').slice(0, 40)}`,
    product: p,
  }));

  const addProduct = (productName) => {
    if (selectedProducts.length >= 4) { toast.error("Maximum 4 produits"); return; }
    if (selectedProducts.find(p => p.name === productName)) { toast.error("Produit deja selectionne"); return; }
    const found = allProducts.find(p => p.name === productName);
    if (found) {
      setSelectedProducts(prev => [...prev, found]);
    }
  };

  const addCustomProduct = () => {
    const name = customProductName.trim();
    if (!name) { toast.error("Saisissez un nom de produit"); return; }
    if (selectedProducts.length >= 4) { toast.error("Maximum 4 produits"); return; }
    if (selectedProducts.find(p => p.name === name)) { toast.error("Produit deja selectionne"); return; }
    setSelectedProducts(prev => [...prev, {
      name, amm: "", matiere_active: name, category: "personnalise", source: "custom", sourceLabel: "Saisie libre",
    }]);
    setCustomProductName("");
  };

  const removeProduct = (idx) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== idx));
    setResult(null);
  };

  const handleAnalyze = async () => {
    if (selectedProducts.length < 2) { toast.error("Selectionnez au moins 2 produits"); return; }
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await authFetch(`${API_BASE}/carnet/compatibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          products: selectedProducts.map(p => ({
            name: p.name,
            amm: p.amm || "",
            matiere_active: p.matiere_active || "",
            category: p.category || p.sourceLabel || "",
          })),
          culture: culture || null,
          parcel_id: parcelId || null,
          ilot_id: ilotId || null,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const data = await res.json();
      setResult(data);
      toast.success(`Analyse terminee — Score: ${data.score}`);
    } catch (e) {
      toast.error(e.message || "Erreur d'analyse");
    }
    setAnalyzing(false);
  };

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/compatibility/history`);
      if (res.ok) setHistory(await res.json());
    } catch (err) { console.error('[CompatibilityTab] fetch failed:', err); }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    if (tab === "history") fetchHistory();
  }, [tab, fetchHistory]);

  return (
    <div className="h-full flex flex-col" data-testid="compatibility-tab-root">
      {/* Inner sub-tabs (Analyser / Historique) — boutons simples pour eviter
          la cascade flex Radix qui posait des soucis de scroll iOS Safari */}
      <div className="px-3 pt-2 flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => setTab("analyze")}
          data-testid="compat-tab-analyze"
          className={`text-[11px] py-1.5 px-3 rounded-full inline-flex items-center gap-1 transition ${
            tab === "analyze" ? "bg-emerald-700 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
        >
          <Beaker className="w-3 h-3" /> Analyser
        </button>
        <button
          type="button"
          onClick={() => setTab("history")}
          data-testid="compat-tab-history"
          className={`text-[11px] py-1.5 px-3 rounded-full inline-flex items-center gap-1 transition ${
            tab === "history" ? "bg-emerald-700 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
        >
          <History className="w-3 h-3" /> Historique
        </button>
      </div>

      {/* Single scroll container — bulletproof, used by both analyze and history views */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
        data-testid="compat-analyze-scroll"
      >
        {tab === "analyze" ? (
          <div className="p-4 space-y-4" style={{ paddingBottom: "max(96px, calc(env(safe-area-inset-bottom, 0px) + 96px))" }}>
            {/* Product selector */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>
                Produits a melanger ({selectedProducts.length}/4)
              </Label>
              {selectedProducts.length < 4 && (
                <SearchableSelect
                  items={searchItems.filter(si => !selectedProducts.find(sp => sp.name === si.value))}
                  value=""
                  onSelect={addProduct}
                  placeholder="Rechercher un produit (phyto ou biostimulant)..."
                  searchPlaceholder="Nom, AMM ou matiere active..."
                  testId="compat-product-select"
                  className="mt-1"
                />
              )}
              {selectedProducts.length < 4 && (
                <div className="flex gap-2 mt-2">
                  <div className="flex-1 relative">
                    <Input
                      value={customProductName}
                      onChange={e => setCustomProductName(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addCustomProduct()}
                      placeholder="Ou saisir un produit libre (ex: Uree, KCl...)"
                      className="h-9 text-sm bg-white border-black/10 pl-8"
                      data-testid="compat-custom-product-input"
                    />
                    <PenLine className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#AAAAAA" }} />
                  </div>
                  <Button variant="outline" size="sm" className="h-9 px-3 text-xs font-semibold rounded-lg border-black/10 gap-1"
                    onClick={addCustomProduct} disabled={!customProductName.trim()} data-testid="compat-add-custom-btn">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </Button>
                </div>
              )}
            </div>

            {/* Selected products chips */}
            {selectedProducts.length > 0 && (
              <div className="space-y-1.5">
                {selectedProducts.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2 p-2.5 rounded-lg border border-black/5" style={{ background: "#F8FAFC" }} data-testid={`compat-product-${i}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: ["#2D6A4F", "#0891B2", "#8B5E3C", "#6366F1"][i] }}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: "#1A1A1A" }}>{p.name}</p>
                      <p className="text-[10px] truncate" style={{ color: "#7A7A7A" }}>
                        {p.source === "custom" ? (
                          <Badge className="text-[8px] border-0 px-1 py-0 mr-1" style={{ background: "#8B5E3C15", color: "#8B5E3C" }}>Saisie libre</Badge>
                        ) : null}
                        {p.amm || ""} {p.matiere_active ? `${p.source !== "custom" ? "- " : ""}${p.matiere_active.slice(0, 40)}` : ""}
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeProduct(i)}>
                      <X className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Culture */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Culture (optionnel)</Label>
              <Input value={culture} onChange={e => setCulture(e.target.value)} placeholder="Ex: Canne a sucre"
                className="h-9 text-sm bg-white border-black/10 mt-1" data-testid="compat-culture-input" />
            </div>

            {/* Analyze button */}
            <Button onClick={handleAnalyze} disabled={analyzing || selectedProducts.length < 2}
              className="w-full h-11 text-sm font-semibold rounded-xl text-white gap-2"
              style={{ background: selectedProducts.length >= 2 ? "#2D6A4F" : "#CCCCCC" }}
              data-testid="compat-analyze-btn">
              {analyzing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Analyse IA en cours...</>
              ) : (
                <><Beaker className="w-4 h-4" /> Analyser la compatibilite ({selectedProducts.length} produits)</>
              )}
            </Button>

            {/* Result */}
            {result && (
              <div className="space-y-4 pt-2 border-t border-black/5" data-testid="compat-result">
                {/* Score gauge */}
                <ScoreGauge score={result.score} />

                {/* Verdict */}
                <div className="p-3 rounded-lg" style={{
                  background: result.score <= -20 ? "#FEF2F2" : result.score <= 20 ? "#FFFBEB" : "#F0FDF4",
                  borderLeft: `3px solid ${result.score <= -20 ? "#DC2626" : result.score <= 20 ? "#EAB308" : "#16A34A"}`,
                }}>
                  <p className="text-xs font-bold mb-1" style={{ color: "#1A1A1A" }}>{result.verdict}</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: "#4A4A4A" }}>{result.resume}</p>
                </div>

                {/* ===== ANALYSE CHIMIQUE ===== */}
                {result.analyse_chimique && (
                  <div className="rounded-xl border border-black/5 overflow-hidden" data-testid="compat-analyse-chimie">
                    <div className="px-3 py-2 flex items-center gap-2" style={{ background: "#EFF6FF" }}>
                      <Beaker className="w-3.5 h-3.5" style={{ color: "#1D4ED8" }} />
                      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#1D4ED8" }}>
                        Analyse chimique
                      </p>
                      {result.analyse_chimique.verdict_chimie && (
                        <Badge className="ml-auto text-[9px] border-0 px-2 py-0" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                          {result.analyse_chimique.verdict_chimie}
                        </Badge>
                      )}
                    </div>
                    <div className="p-3 space-y-1.5 bg-white">
                      {result.analyse_chimique.explication && (
                        <p className="text-[11px] leading-relaxed mb-2" style={{ color: "#1A1A1A" }}>
                          {result.analyse_chimique.explication}
                        </p>
                      )}
                      {[
                        ["pH compatibilité", result.analyse_chimique.ph_compatibilite],
                        ["Formulations", result.analyse_chimique.formulations],
                        ["Stabilité molécules", result.analyse_chimique.stabilite_molecules],
                        ["Ordre d'ajout", result.analyse_chimique.ordre_addition],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex flex-col sm:flex-row gap-0.5 sm:gap-2 text-[11px] py-1 border-b last:border-0" style={{ borderColor: "#EFF6FF" }}>
                          <span className="sm:w-32 font-semibold shrink-0" style={{ color: "#1D4ED8" }}>{k}</span>
                          <span className="leading-snug" style={{ color: "#374151" }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== ANALYSE PHYTOTOXICITÉ / COMPLÉMENTARITÉ ===== */}
                {result.analyse_phytotoxicite && (
                  <div className="rounded-xl border border-black/5 overflow-hidden" data-testid="compat-analyse-phyto">
                    <div className="px-3 py-2 flex items-center gap-2" style={{ background: "#F0FDF4" }}>
                      <Sprout className="w-3.5 h-3.5" style={{ color: "#16A34A" }} />
                      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#16A34A" }}>
                        Phytotoxicité &amp; complémentarité
                      </p>
                      {result.analyse_phytotoxicite.verdict_plante && (
                        <Badge className="ml-auto text-[9px] border-0 px-2 py-0" style={{ background: "#DCFCE7", color: "#16A34A" }}>
                          {result.analyse_phytotoxicite.verdict_plante}
                        </Badge>
                      )}
                    </div>
                    <div className="p-3 space-y-1.5 bg-white">
                      {result.analyse_phytotoxicite.explication && (
                        <p className="text-[11px] leading-relaxed mb-2" style={{ color: "#1A1A1A" }}>
                          {result.analyse_phytotoxicite.explication}
                        </p>
                      )}
                      {[
                        ["Complémentarité", result.analyse_phytotoxicite.complementarite],
                        ["Risques brûlures", result.analyse_phytotoxicite.risques_brulures],
                        ["Effet sur la culture", result.analyse_phytotoxicite.effet_culture],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex flex-col sm:flex-row gap-0.5 sm:gap-2 text-[11px] py-1 border-b last:border-0" style={{ borderColor: "#F0FDF4" }}>
                          <span className="sm:w-32 font-semibold shrink-0" style={{ color: "#16A34A" }}>{k}</span>
                          <span className="leading-snug" style={{ color: "#374151" }}>{v}</span>
                        </div>
                      ))}
                      {result.analyse_phytotoxicite.synergies?.length > 0 && (
                        <div className="pt-2 mt-1.5 border-t border-emerald-100">
                          <p className="text-[10px] font-bold mb-1" style={{ color: "#16A34A" }}>Synergies</p>
                          {result.analyse_phytotoxicite.synergies.map((s, idx) => (
                            <p key={`syn-${idx}`} className="text-[11px] flex items-start gap-1.5 mb-0.5" style={{ color: "#374151" }}>
                              <span className="text-emerald-600 mt-0.5">+</span>{s}
                            </p>
                          ))}
                        </div>
                      )}
                      {result.analyse_phytotoxicite.antagonismes?.length > 0 && (
                        <div className="pt-2 mt-1.5 border-t border-emerald-100">
                          <p className="text-[10px] font-bold mb-1" style={{ color: "#DC2626" }}>Antagonismes</p>
                          {result.analyse_phytotoxicite.antagonismes.map((a, idx) => (
                            <p key={`ant-${idx}`} className="text-[11px] flex items-start gap-1.5 mb-0.5" style={{ color: "#374151" }}>
                              <span className="text-red-600 mt-0.5">−</span>{a}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Interactions */}
                {result.interactions?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#4A4A4A" }}>
                      Interactions ({result.interactions.length})
                    </p>
                    <div className="space-y-2">
                      {result.interactions.map((intx, i) => (
                        <InteractionCard key={`intx-${intx.between?.join('-') || i}`} interaction={intx} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Conditions melange */}
                {result.conditions_melange && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#2D6A4F" }}>Conditions de melange</p>
                    <div className="p-3 rounded-lg space-y-1.5" style={{ background: "#F0FDF4" }}>
                      {[
                        ["pH optimal", result.conditions_melange.ph_optimal],
                        ["Temperature", result.conditions_melange.temperature],
                        ["Ordre de melange", result.conditions_melange.ordre_melange],
                        ["Delai utilisation", result.conditions_melange.delai_utilisation],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex text-[11px]">
                          <span className="w-28 shrink-0 font-medium" style={{ color: "#2D6A4F" }}>{k}</span>
                          <span style={{ color: "#1A1A1A" }}>{v}</span>
                        </div>
                      ))}
                      {result.conditions_melange.precautions?.length > 0 && (
                        <div className="pt-1.5 mt-1.5 border-t" style={{ borderColor: "#2D6A4F20" }}>
                          {result.conditions_melange.precautions.map((p) => (
                            <p key={`prec-${p.slice(0, 30)}`} className="text-[10px] flex items-start gap-1.5 mb-0.5">
                              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" style={{ color: "#EAB308" }} />
                              <span style={{ color: "#4A4A4A" }}>{p}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Avertissements */}
                {result.avertissements?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#DC2626" }}>Avertissements</p>
                    {result.avertissements.map((a) => (
                      <div key={`warn-${a.slice(0, 30)}`} className="flex items-start gap-2 p-2 rounded-lg mb-1" style={{ background: "#FEF2F2" }}>
                        <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#DC2626" }} />
                        <p className="text-[11px]" style={{ color: "#991B1B" }}>{a}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommandation */}
                {result.recommandation && (
                  <div className="p-3 rounded-lg" style={{ background: "#F8FAFC" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#4A4A4A" }}>Recommandation</p>
                    <p className="text-[11px] leading-relaxed" style={{ color: "#1A1A1A" }}>{result.recommandation}</p>
                  </div>
                )}

                <p className="text-[9px] italic pt-2" style={{ color: "#AAAAAA" }}>
                  Analyse generee par IA (GPT-5.2). Verifiez toujours avec les fiches techniques officielles et votre conseiller agricole avant application.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div
            className="p-4 space-y-3"
            style={{ paddingBottom: "max(96px, calc(env(safe-area-inset-bottom, 0px) + 96px))" }}
          >
          {loadingHistory ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>
          ) : history.length === 0 ? (
            <div className="text-center py-16 px-4">
              <History className="w-8 h-8 mx-auto mb-3 opacity-20" />
              <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucune analyse precedente</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {history.map(h => {
                const expanded = expandedHistoryId === h.analysis_id;
                const score = h.result?.score || 0;
                const scoreColor = score <= -20 ? "#DC2626" : score <= 20 ? "#EAB308" : "#16A34A";
                return (
                  <div key={h.analysis_id} className="rounded-xl border border-black/5 overflow-hidden" data-testid={`compat-history-${h.analysis_id}`}>
                    <button onClick={() => setExpandedHistoryId(expanded ? null : h.analysis_id)}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/[0.02]">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2"
                        style={{ borderColor: scoreColor + "40", background: scoreColor + "10" }}>
                        <span className="text-xs font-bold" style={{ color: scoreColor }}>{score > 0 ? "+" : ""}{score}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: "#1A1A1A" }}>
                          {h.products?.map(p => p.name).join(" + ")}
                        </p>
                        <p className="text-[10px]" style={{ color: "#7A7A7A" }}>
                          {h.result?.verdict} — {new Date(h.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      {expanded ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
                    </button>
                    {expanded && h.result && (
                      <div className="px-3 pb-3 border-t border-black/5 pt-2 space-y-2">
                        <ScoreGauge score={h.result.score} />
                        <p className="text-[11px]" style={{ color: "#4A4A4A" }}>{h.result.resume}</p>
                        {h.result.recommandation && (
                          <p className="text-[11px] italic" style={{ color: "#7A7A7A" }}>{h.result.recommandation}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}
